import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Camera, Check, CloudUpload, ImagePlus, LockKeyhole, MessageCircle, Mic, MicOff, RotateCcw, Sparkles, UploadCloud } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { getRecommendation, occasionOptions, styles, type FaceShape } from '@/data/styles';
import { AdjustmentPanel } from '@/components/AdjustmentPanel';
import { ColourPicker, turbanColours, type TurbanColour } from '@/components/ColourPicker';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { trackEvent } from '@/lib/analytics';

type Step = 'upload' | 'analysis' | 'style' | 'generating' | 'result';
type SpeechRecognitionResult = { transcript: string };
type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  onstart: (() => void) | null;
  onresult: ((event: { results: ArrayLike<ArrayLike<SpeechRecognitionResult>> }) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
};
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;
type FaceDetectorLike = { detect: (image: CanvasImageSource) => Promise<Array<{ boundingBox: { width: number; height: number } }>> };
type FaceDetectorConstructor = new (options?: { fastMode?: boolean; maxDetectedFaces?: number }) => FaceDetectorLike;

function readLooks() {
  try {
    const savedLooks = JSON.parse(localStorage.getItem('turbans-looks') || '[]');
    return Array.isArray(savedLooks) ? savedLooks : [];
  } catch {
    return [];
  }
}

function saveLooks(looks: unknown[]) {
  try {
    localStorage.setItem('turbans-looks', JSON.stringify(looks));
    return true;
  } catch {
    return false;
  }
}

const defaultFreeTryLimit = 3;

function readTryCount() {
  try {
    const count = Number.parseInt(localStorage.getItem('turbans-free-tries') || '0', 10);
    return Number.isFinite(count) ? Math.max(0, Math.min(defaultFreeTryLimit, count)) : 0;
  } catch {
    return 0;
  }
}

function saveTryCount(count: number) {
  try {
    localStorage.setItem('turbans-free-tries', String(count));
  } catch {
    // The server remains the authoritative limit if localStorage is unavailable.
  }
}

function SubscriptionPrompt({ open, onOpenChange, freeTryLimit }: { open: boolean; onOpenChange: (open: boolean) => void; freeTryLimit: number }) {
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="overflow-hidden border-primary/20 p-0 sm:max-w-[520px]" data-testid="subscription-prompt">
      <div className="bg-primary px-6 py-7 text-primary-foreground sm:px-8">
        <p className="mono-font text-[10px] uppercase tracking-[.18em] text-secondary">Your {freeTryLimit} free try-ons are complete</p>
        <DialogHeader className="mt-4 text-left">
          <DialogTitle className="display-font text-4xl leading-none text-primary-foreground sm:text-5xl">Keep exploring with Pro.</DialogTitle>
          <DialogDescription className="mt-4 max-w-md text-sm leading-6 text-primary-foreground/75">Your final free result is ready. Choose Pro to continue creating personalized try-ons, colours, and wedding looks.</DialogDescription>
        </DialogHeader>
      </div>
      <div className="p-6 sm:p-8">
        <div className="grid gap-3 text-sm text-muted-foreground sm:grid-cols-3">
          {['More try-ons', 'Monthly access', 'Store-managed billing'].map((benefit) => <div key={benefit} className="rounded-xl bg-muted px-3 py-3 text-center font-semibold">{benefit}</div>)}
        </div>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={() => onOpenChange(false)} className="rounded-xl border border-border px-4 py-3 text-sm font-bold text-foreground transition hover:border-primary hover:text-primary" data-testid="button-view-third-result">View my result</button>
          <Link href="/pricing" onClick={() => onOpenChange(false)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-secondary px-4 py-3 text-sm font-bold text-secondary-foreground transition hover:shadow-md" data-testid="link-subscription-plans">View subscription plans <ArrowRight size={15} /></Link>
        </div>
      </div>
    </DialogContent>
  </Dialog>;
}

async function optimisePhoto(file: File) {
  const source = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const preview = new Image();
      preview.onload = () => resolve(preview);
      preview.onerror = () => reject(new Error('Could not load image'));
      preview.src = source;
    });
    const longestEdge = Math.max(image.naturalWidth, image.naturalHeight);
    const scale = Math.min(1, 1600 / longestEdge);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.9);
  } catch (error) {
    throw new Error('Could not load image', { cause: error });
  } finally {
    URL.revokeObjectURL(source);
  }
}

async function detectFaceShape(dataUrl: string): Promise<FaceShape> {
  const detectorWindow = window as Window & { FaceDetector?: FaceDetectorConstructor };
  if (!detectorWindow.FaceDetector) return 'oval';
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const preview = new Image();
      preview.onload = () => resolve(preview);
      preview.onerror = () => reject(new Error('Could not inspect selfie'));
      preview.src = dataUrl;
    });
    const faces = await new detectorWindow.FaceDetector({ fastMode: true, maxDetectedFaces: 3 }).detect(image);
    const face = faces
      .map((candidate) => candidate.boundingBox)
      .sort((first, second) => second.width * second.height - first.width * first.height)[0];
    if (!face) return 'oval';
    const ratio = face.width / face.height;
    if (ratio > 0.84) return 'round';
    if (ratio < 0.64) return 'long';
    return 'oval';
  } catch {
    return 'oval';
  }
}

export default function Upload() {
  const [, navigate] = useLocation();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const editOperationRef = useRef(0);
  const analysisTimeoutRef = useRef<number | null>(null);
  const generationAbortRef = useRef<AbortController | null>(null);
  const [step, setStep] = useState<Step>('upload');
  const [photo, setPhoto] = useState<string | null>(null);
  const [generatedPhoto, setGeneratedPhoto] = useState<string | null>(null);
  const [activeLookId, setActiveLookId] = useState<string | null>(null);
  const [correction, setCorrection] = useState('');
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [adjustmentStatus, setAdjustmentStatus] = useState('');
  const [adjustmentError, setAdjustmentError] = useState('');
  const [consent, setConsent] = useState(false);
  const [styleId, setStyleId] = useState('patiala-shahi');
  const [recommendedStyleId, setRecommendedStyleId] = useState<string | null>(null);
  const [colour, setColour] = useState('#7f263b');
  const [colourName, setColourName] = useState('Deep Maroon');
  const [recolourToken, setRecolourToken] = useState<string | null>(null);
  const [occasion, setOccasion] = useState('everyday');
  const [faceShape, setFaceShape] = useState<FaceShape>('oval');
  const [request, setRequest] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [voiceMessage, setVoiceMessage] = useState('');
  const [storageNotice, setStorageNotice] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [status, setStatus] = useState('Matching your outfit…');
  const [tryCount, setTryCount] = useState(readTryCount);
  const [freeTryLimit, setFreeTryLimit] = useState(defaultFreeTryLimit);
  const [isSubscriptionPromptOpen, setIsSubscriptionPromptOpen] = useState(false);

  useEffect(() => () => {
    recognitionRef.current?.stop();
    if (analysisTimeoutRef.current !== null) window.clearTimeout(analysisTimeoutRef.current);
    generationAbortRef.current?.abort();
  }, []);
  useEffect(() => {
    let active = true;
    fetch('/api/try-on/status')
      .then((response) => response.json())
      .then((result) => {
        if (!active || typeof result.freeTryCount !== 'number') return;
        const nextLimit = typeof result.freeTryLimit === 'number' && result.freeTryLimit >= defaultFreeTryLimit
          ? result.freeTryLimit
          : defaultFreeTryLimit;
        const nextCount = Math.max(0, Math.min(nextLimit, result.freeTryCount));
        setFreeTryLimit(nextLimit);
        setTryCount(nextCount);
        saveTryCount(nextCount);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);

  const syncTryCount = (value: unknown, limitValue?: unknown) => {
    const nextLimit = typeof limitValue === 'number' && limitValue >= defaultFreeTryLimit
      ? limitValue
      : freeTryLimit;
    const nextCount = typeof value === 'number'
      ? Math.max(0, Math.min(nextLimit, value))
      : Math.min(nextLimit, tryCount + 1);
    setFreeTryLimit(nextLimit);
    setTryCount(nextCount);
    saveTryCount(nextCount);
    if (nextCount >= nextLimit) {
      if (tryCount < nextLimit) setIsSubscriptionPromptOpen(true);
    }
  };

  const onFile = async (file?: File) => {
    if (!file || !file.type.startsWith('image/')) {
      setGenerationError('Please choose a photo file to continue.');
      return;
    }
    let optimisedPhoto: string;
    try {
      optimisedPhoto = await optimisePhoto(file);
    } catch {
      setGenerationError('We couldn’t open that photo. Please choose a clear JPEG, PNG, or WEBP image.');
      return;
    }
    editOperationRef.current += 1;
    setPhoto(optimisedPhoto);
    setGeneratedPhoto(null);
    setActiveLookId(null);
    setRecommendedStyleId(null);
    setCorrection('');
    setAdjustmentStatus('');
    setAdjustmentError('');
    setGenerationError('');
    setStep('upload');
  };

  const toggleVoice = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }
    const speechWindow = window as Window & { SpeechRecognition?: SpeechRecognitionConstructor; webkitSpeechRecognition?: SpeechRecognitionConstructor };
    const Recognition = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
    if (!Recognition) {
      setVoiceMessage('Voice input is not available in this browser. You can type your request instead.');
      return;
    }
    const recognition = new Recognition();
    recognition.lang = 'en-IN';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.onstart = () => { setIsListening(true); setVoiceMessage('Listening… tell us what you’re imagining.'); };
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results).map((result) => result[0]?.transcript ?? '').join(' ').trim();
      if (transcript) setRequest((current) => current ? `${current} ${transcript}` : transcript);
    };
    recognition.onerror = (event) => {
      setIsListening(false);
      setVoiceMessage(event.error === 'not-allowed' ? 'Microphone access was blocked. You can type your request instead.' : 'We couldn’t catch that. Try again or type your request.');
    };
    recognition.onend = () => { setIsListening(false); recognitionRef.current = null; };
    recognitionRef.current = recognition;
    setVoiceMessage('');
    recognition.start();
  };

  const analyse = () => {
    if (!photo || !consent) return;
    const words = request.toLowerCase();
    const requestedStyle = styles.find((item) => {
      const aliases = [item.name.toLowerCase(), item.id.replaceAll('-', ' ')];
      if (item.id === 'turban-with-larr') aliases.push('larr');
      return aliases.some((alias) => words.includes(alias));
    });
    setStep('analysis');
    if (analysisTimeoutRef.current !== null) window.clearTimeout(analysisTimeoutRef.current);
    analysisTimeoutRef.current = window.setTimeout(async () => {
      analysisTimeoutRef.current = null;
      const detectedShape = await detectFaceShape(photo);
      setFaceShape(detectedShape);
      const inferredOutfit = words.includes('black') ? 'black' : words.includes('denim') || words.includes('blue') ? 'denim' : words.includes('cream') || words.includes('ivory') || words.includes('white') ? 'cream' : words.includes('brown') || words.includes('earth') ? 'earth' : 'neutrals';
      const matchedStyle = requestedStyle ?? getRecommendation(detectedShape, occasion as Parameters<typeof getRecommendation>[1], inferredOutfit).style;
      setStyleId(matchedStyle.id);
      setRecommendedStyleId(matchedStyle.id);
      setStep('style');
    }, 900);
  };

  const generate = async () => {
    if (!photo) return;
    if (tryCount >= freeTryLimit) {
      setIsSubscriptionPromptOpen(true);
      return;
    }
    editOperationRef.current += 1;
    setGenerationError('');
    setAdjustmentStatus('');
    setAdjustmentError('');
    setCorrection('');
    setStep('generating');
    setStatus('Preparing your selfie for a realistic edit…');
    const styleStatusTimer = window.setTimeout(() => setStatus('Shaping your turban with natural folds and fit…'), 900);
    const resultStatusTimer = window.setTimeout(() => setStatus('Finishing your identity-preserving try-on…'), 2400);
    const chosen = styles.find((item) => item.id === styleId) ?? styles[0];
    const selectedColour = turbanColours.find((item) => item.name === colourName && item.hex === colour) ?? turbanColours[0];
    const tryOnRequest = [
      `Turban style: ${chosen.name}.`,
      `Exact construction: ${chosen.visualSpec}`,
      `Colour: ${colourName} (${colour}).`,
      `Occasion: ${occasionLabel}.`,
      `Face profile: ${faceShape}.`,
      'Automatically locate the main visible face and head before placing the turban.',
      request ? `Personal request: ${request.trim()}` : '',
    ].filter(Boolean).join(' ');
    generationAbortRef.current?.abort();
    const controller = new AbortController();
    generationAbortRef.current = controller;

    try {
      const response = await fetch('/api/try-on', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageDataUrl: photo,
          request: tryOnRequest,
          style: { id: chosen.id, name: chosen.name },
          colour: selectedColour,
        }),
        signal: controller.signal,
      });
      const result = await response.json().catch(() => ({}));
      if (result.code === 'FREE_TRY_LIMIT') {
        syncTryCount(result.freeTryCount, result.freeTryLimit);
        setIsSubscriptionPromptOpen(true);
        setStep('style');
        return;
      }
      if (!response.ok || typeof result.imageDataUrl !== 'string') {
        if (result.code === 'COLOUR_MASK_RETRYABLE') {
          trackEvent('colour_mask_retryable', { action: 'initial_generation' });
        }
        throw new Error(typeof result.error === 'string' ? result.error : 'We could not create your try-on image.');
      }
      syncTryCount(result.freeTryCount, result.freeTryLimit);
      if (result.colour) {
        trackEvent('colour_change_succeeded', { action: 'initial_generation' });
      }
      setGeneratedPhoto(result.imageDataUrl);
      setRecolourToken(typeof result.recolourToken === 'string' ? result.recolourToken : null);
      const newLookId = `${Date.now()}`;
      setActiveLookId(newLookId);
      const look = { id: newLookId, photo: result.imageDataUrl, styleId: chosen.id, styleName: chosen.name, colour, colourName, recolourToken: result.recolourToken, colourTarget: result.colour ?? selectedColour, colourQuality: result.colourQuality, occasion, faceShape, request: request.trim(), createdAt: new Date().toISOString() };
      const nextLooks = [look, ...readLooks()].slice(0, 12);
      if (saveLooks(nextLooks)) {
        setStorageNotice('');
      } else {
        const compactLooks = nextLooks.map((savedLook) => ({ ...savedLook, photo: null }));
        const savedCompactLooks = saveLooks(compactLooks);
        setStorageNotice(savedCompactLooks
          ? 'Your look is ready. To save space on this device, the saved version keeps your style brief without the photo.'
          : 'Your look is ready, but this browser could not save it. You can still share it now.');
      }
      setStep('result');
    } catch (error) {
      if (controller.signal.aborted) return;
      setGenerationError(error instanceof Error ? error.message : 'We could not create your try-on image.');
      setStep('style');
    } finally {
      if (generationAbortRef.current === controller) generationAbortRef.current = null;
      window.clearTimeout(styleStatusTimer);
      window.clearTimeout(resultStatusTimer);
    }
  };

  const updateActiveLook = (imageDataUrl: string, updates: Record<string, unknown> = {}) => {
    setGeneratedPhoto(imageDataUrl);
    if (!activeLookId) return;
    const currentLooks = readLooks();
    if (!currentLooks.some((look) => look.id === activeLookId)) {
      setStorageNotice('Your updated look is ready, but the original saved look was not available to update on this device.');
      return;
    }
    const updatedLooks = currentLooks.map((look) => look.id === activeLookId ? { ...look, photo: imageDataUrl, ...updates } : look);
    if (saveLooks(updatedLooks)) {
      setStorageNotice('');
    } else {
      setStorageNotice('Your updated look is ready, but this browser could not update the saved image. You can still share the updated result.');
    }
  };

  const applyCorrection = async () => {
    if (!generatedPhoto || isAdjusting || tryCount >= freeTryLimit) {
      if (tryCount >= freeTryLimit) setIsSubscriptionPromptOpen(true);
      return;
    }
    const operationId = ++editOperationRef.current;
    const chosen = styles.find((item) => item.id === styleId) ?? styles[0];
    setIsAdjusting(true);
    setAdjustmentStatus('');
    setAdjustmentError('');
    try {
      const response = await fetch('/api/try-on', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageDataUrl: generatedPhoto,
          request: `Turban style: ${chosen.name}. Exact construction: ${chosen.visualSpec}.`,
          style: { id: chosen.id, name: chosen.name },
          correction: correction.trim(),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (operationId !== editOperationRef.current) return;
      if (result.code === 'FREE_TRY_LIMIT') {
        syncTryCount(result.freeTryCount, result.freeTryLimit);
        setIsSubscriptionPromptOpen(true);
        return;
      }
      if (!response.ok || typeof result.imageDataUrl !== 'string') {
        throw new Error(typeof result.error === 'string' ? result.error : 'We could not adjust your turban.');
      }
      syncTryCount(result.freeTryCount, result.freeTryLimit);

      updateActiveLook(result.imageDataUrl);
      setCorrection('');
      setAdjustmentStatus('Your corrected turban is ready.');
    } catch (error) {
      if (operationId === editOperationRef.current) setAdjustmentError(error instanceof Error ? error.message : 'We could not adjust your turban.');
    } finally {
      if (operationId === editOperationRef.current) setIsAdjusting(false);
    }
  };

  const applyColourChange = async (nextColour: TurbanColour) => {
    if (!generatedPhoto || isAdjusting || nextColour.name === colourName) return;
    if (!recolourToken) {
      setAdjustmentError('This saved look needs a fresh secure try-on before its colour can be changed.');
      return;
    }
    const operationId = ++editOperationRef.current;
    const chosen = styles.find((item) => item.id === styleId) ?? styles[0];
    setIsAdjusting(true);
    setAdjustmentStatus('');
    setAdjustmentError('');
    try {
      const response = await fetch('/api/try-on', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageDataUrl: generatedPhoto,
          request: `Turban style: ${chosen.name}. Exact construction: ${chosen.visualSpec}.`,
          style: { id: chosen.id, name: chosen.name },
          colour: nextColour,
          editMode: 'recolour',
          recolourToken,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (operationId !== editOperationRef.current) return;
      if (result.code === 'FREE_TRY_LIMIT') {
        syncTryCount(result.freeTryCount, result.freeTryLimit);
        setIsSubscriptionPromptOpen(true);
        return;
      }
      if (!response.ok || typeof result.imageDataUrl !== 'string') {
        if (result.code === 'COLOUR_MASK_RETRYABLE') {
          trackEvent('colour_mask_retryable', { action: 'result_recolour' });
        }
        throw new Error(result.code === 'COLOUR_MASK_RETRYABLE'
          ? 'The turban fabric needs another colour pass. Your current look is unchanged — please select that colour again.'
          : typeof result.error === 'string' ? result.error : 'We could not change the turban colour.');
      }
      trackEvent('colour_change_succeeded', { action: 'result_recolour' });
      setColour(nextColour.hex);
      setColourName(nextColour.name);
      setRecolourToken(typeof result.recolourToken === 'string' ? result.recolourToken : null);
      updateActiveLook(result.imageDataUrl, { colour: nextColour.hex, colourName: nextColour.name, recolourToken: result.recolourToken, colourTarget: result.colour ?? nextColour, colourQuality: result.colourQuality });
      setAdjustmentStatus(`Your ${nextColour.name.toLowerCase()} turban is ready.`);
    } catch (error) {
      if (operationId === editOperationRef.current) setAdjustmentError(error instanceof Error ? error.message : 'We could not change the turban colour.');
    } finally {
      if (operationId === editOperationRef.current) setIsAdjusting(false);
    }
  };

  const tryAnother = () => {
    if (isAdjusting) return;
    editOperationRef.current += 1;
    setRecolourToken(null);
    setStep('style');
  };

  const chosenStyle = styles.find((item) => item.id === styleId) ?? styles[0];
  const occasionLabel = occasionOptions.find((item) => item.value === occasion)?.label ?? 'Everyday';
  const stepNumber = step === 'upload' ? '01' : step === 'analysis' ? '02' : step === 'style' ? '03' : '04';
  const exampleRequests = ['A tall Patiala Shahi in saffron', 'Classic navy with a visible larr', 'A soft Gol Pagg for the weekend'];

  return <div className="page-enter bg-background">
    <SubscriptionPrompt open={isSubscriptionPromptOpen} onOpenChange={setIsSubscriptionPromptOpen} freeTryLimit={freeTryLimit} />
    <section className="border-b border-border/70 bg-card/50">
      <div className="mx-auto max-w-[1100px] px-5 py-12 sm:px-8 lg:px-12 lg:py-16">
        <div className="flex items-center justify-between">
          <div><p className="mono-font text-[10px] uppercase tracking-[.18em] text-primary">Find your look · {stepNumber} / 04</p><h1 className="display-font mt-4 text-5xl leading-[.92] tracking-[-.05em] sm:text-7xl">{step === 'result' ? <>This is your<br /><em className="not-italic text-primary">look.</em></> : <>Find the turban<br /><em className="not-italic text-primary">that fits.</em></>}</h1></div>
          <div className="hidden items-center gap-2 text-right text-xs text-muted-foreground sm:flex"><LockKeyhole size={15} className="text-primary" /><span>Your photos stay private<br />and can be deleted anytime.</span></div>
        </div>
      </div>
    </section>
    <main className="mx-auto max-w-[1100px] px-5 py-10 sm:px-8 lg:px-12 lg:py-16">
      {step === 'upload' && <div className="grid gap-10 lg:grid-cols-[1fr_.78fr]">
        <div>
          <p className="max-w-[480px] text-base leading-7 text-muted-foreground">Upload a clear selfie in the outfit you want to match. Then tell your AI stylist what kind of turban you’re imagining.</p>
           <div className={`mt-8 flex min-h-[290px] w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center transition ${photo ? 'border-primary bg-primary/5' : 'border-border bg-card'}`} data-testid="dropzone-upload">
            {photo ? <img src={photo} alt="Your selfie preview" className="max-h-[240px] rounded-xl object-contain shadow-sm" /> : <><span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary/20 text-primary"><CloudUpload size={28} /></span><span className="mt-5 text-lg font-semibold">Upload your selfie</span><span className="mt-2 text-sm text-muted-foreground">Tap to choose a photo or use your camera</span><span className="mt-5 mono-font text-[10px] uppercase tracking-[.12em] text-muted-foreground">JPG · JPEG · PNG · WEBP</span></>}
           </div>
           <div className="mt-4 grid gap-3 sm:grid-cols-2">
             <button type="button" onClick={() => cameraInputRef.current?.click()} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3.5 text-sm font-bold text-primary-foreground transition hover:shadow-md" data-testid="button-take-photo"><Camera size={17} /> {photo ? 'Retake photo' : 'Take a photo'}</button>
             <button type="button" onClick={() => galleryInputRef.current?.click()} className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-5 py-3.5 text-sm font-bold text-foreground transition hover:border-primary hover:text-primary" data-testid="button-choose-gallery"><ImagePlus size={17} /> Choose from gallery</button>
           </div>
           <input ref={cameraInputRef} className="hidden" type="file" accept="image/jpeg,image/png,image/webp" capture="user" onChange={(event) => onFile(event.target.files?.[0])} />
           <input ref={galleryInputRef} className="hidden" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => onFile(event.target.files?.[0])} />
           {photo && <button onClick={() => { setPhoto(null); if (cameraInputRef.current) cameraInputRef.current.value = ''; if (galleryInputRef.current) galleryInputRef.current.value = ''; }} className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-primary"><RotateCcw size={13} /> Remove photo</button>}
          <div className="mt-7 rounded-2xl border border-border bg-card p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4"><div><p className="mono-font text-[10px] uppercase tracking-[.16em] text-primary">Talk to your AI stylist</p><h2 className="display-font mt-2 text-2xl">Describe the look you want.</h2></div><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary/20 text-primary"><MessageCircle size={17} /></span></div>
            <div className="relative mt-5"><label htmlFor="turban-request" className="sr-only">Describe the turban you want</label><textarea id="turban-request" value={request} onChange={(event) => { setRequest(event.target.value); setVoiceMessage(''); }} placeholder="Try: I want a tall Patiala style in navy with a clean, wide fan…" rows={4} className="w-full resize-none rounded-xl border border-border bg-background px-4 py-3 pr-14 text-sm leading-6 outline-none transition placeholder:text-muted-foreground/70 focus:border-primary focus:ring-2 focus:ring-primary/10" data-testid="input-turban-request" /><button type="button" onClick={toggleVoice} className={`absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full transition ${isListening ? 'animate-pulse bg-accent text-accent-foreground' : 'bg-muted text-primary hover:bg-secondary'}`} aria-label={isListening ? 'Stop listening' : 'Describe your turban by voice'} aria-pressed={isListening} data-testid="button-voice-request">{isListening ? <MicOff size={17} /> : <Mic size={17} />}</button></div>
            <div className="mt-3 flex flex-wrap gap-2">{exampleRequests.map((example) => <button key={example} type="button" onClick={() => setRequest(example)} className="rounded-full border border-border bg-background px-3 py-1.5 text-left text-[11px] font-semibold text-muted-foreground transition hover:border-primary hover:text-primary">{example}</button>)}</div>
            <p className={`mt-3 text-xs ${voiceMessage ? 'text-primary' : 'text-muted-foreground'}`} role="status" aria-live="polite">{voiceMessage || 'Type it out, or tap the mic and say it naturally.'}</p>
            <label htmlFor="occasion" className="mt-5 block text-xs font-semibold text-muted-foreground">What’s the moment?</label>
            <select id="occasion" value={occasion} onChange={(event) => setOccasion(event.target.value)} className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm font-semibold text-foreground outline-none focus:border-primary" data-testid="select-occasion">{occasionOptions.map((item) => <option key={item.value} value={item.value}>{item.label} — {item.note}</option>)}</select>
          </div>
          <label className="mt-7 flex cursor-pointer items-start gap-3 text-sm leading-6 text-muted-foreground"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]" data-testid="checkbox-photo-consent" /><span>By uploading a selfie, I confirm I have permission to use this image and consent to it being processed to create my Turbans.ai look.</span></label>
          <button onClick={analyse} disabled={!photo || !consent} className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-xl bg-primary px-6 py-4 text-sm font-bold text-primary-foreground transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto" data-testid="button-analyse-outfit">Analyse my selfie <ArrowRight size={16} /></button>
        </div>
        <aside className="rounded-2xl bg-primary p-7 text-primary-foreground sm:p-9"><p className="mono-font text-[10px] uppercase tracking-[.16em] text-secondary">For the best result</p><h2 className="display-font mt-4 text-3xl">Let the selfie<br />do the talking.</h2><ul className="mt-8 space-y-4">{['Clear face and good lighting', 'Front-facing, upper body visible', 'Kurta, suit, sherwani, or whatever you’re wearing', 'No hat or turban if possible'].map((item) => <li key={item} className="flex items-center gap-3 text-sm text-primary-foreground/75"><Check size={16} className="shrink-0 text-secondary" />{item}</li>)}</ul><div className="mt-10 border-t border-primary-foreground/15 pt-5 text-xs leading-5 text-primary-foreground/55"><LockKeyhole size={14} className="mb-2 text-secondary" />Your image is only used to create your requested look. We never publish it without permission.</div></aside>
      </div>}
      {step === 'analysis' && <div className="mx-auto max-w-xl py-12 text-center"><div className="mx-auto flex h-20 w-20 animate-pulse items-center justify-center rounded-full bg-secondary/25 text-primary"><Sparkles size={30} /></div><h2 className="display-font mt-8 text-4xl">Reading your selfie…</h2><p className="mt-4 text-muted-foreground">Scanning face proportions, outfit tones, occasion, and the direction you described.</p><div className="mx-auto mt-8 h-1.5 max-w-xs overflow-hidden rounded-full bg-muted"><div className="h-full w-2/3 animate-pulse rounded-full bg-primary" /></div></div>}
        {step === 'style' && <div className="grid gap-10 lg:grid-cols-[.75fr_1.25fr]"><div>{photo ? <img src={photo} alt="Your uploaded selfie" className="max-h-[520px] w-full rounded-2xl bg-muted object-contain" /> : <div className="rounded-2xl bg-muted p-12 text-center"><ImagePlus className="mx-auto" /></div>}<div className="mt-5 rounded-xl border border-border bg-card p-5"><p className="mono-font text-[10px] uppercase tracking-[.16em] text-primary">Automatic face fit · {occasionLabel}</p><p className="mt-3 text-sm leading-6 text-muted-foreground">AI will locate the main visible face and head in your photo, then fit the turban automatically without cropping your original image.</p>{request && <p className="mt-2 border-t border-border pt-3 text-sm leading-6 text-foreground">“{request}”</p>}</div></div><div><div className="rounded-2xl border border-secondary/40 bg-secondary/10 p-5"><p className="mono-font text-[10px] uppercase tracking-[.15em] text-primary">AI best match</p><h2 className="display-font mt-2 text-3xl">{chosenStyle.name}</h2><p className="mt-2 text-sm leading-6 text-muted-foreground">Chosen for your face profile, outfit tones, and {occasionLabel.toLowerCase()} plans. It is ready to generate—only change it if you have a personal preference.</p></div><h2 className="display-font mt-9 text-3xl">Choose a colour</h2><div className="mt-4 flex flex-wrap gap-3">{turbanColours.map((tone) => <button key={tone.name} onClick={() => { setColour(tone.hex); setColourName(tone.name); }} className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${colourName === tone.name ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card hover:border-primary/50'}`} data-testid={`button-colour-${tone.name.toLowerCase().replaceAll(' ', '-')}`}><span className="h-5 w-5 rounded-full border border-background shadow-sm" style={{ backgroundColor: tone.hex }} />{tone.name}{colourName === tone.name && <Check size={14} />}</button>)}</div><h2 className="display-font mt-10 text-3xl">Other silhouettes</h2><p className="mt-2 text-sm text-muted-foreground">The highlighted option is AI’s match. You can still choose another style.</p><div className="mt-4 grid gap-3 sm:grid-cols-2">{styles.map((item) => <button key={item.id} onClick={() => setStyleId(item.id)} className={`flex items-center gap-4 rounded-xl border p-3 text-left transition ${styleId === item.id ? 'border-primary bg-primary/10' : 'border-border bg-card hover:border-primary/50'}`} data-testid={`button-style-${item.id}`}><img src={item.image} alt={`${item.name} turban reference`} className="h-16 w-16 shrink-0 rounded-lg object-cover object-[center_20%]" /><span><strong className="flex items-center gap-2 text-sm">{item.name}{recommendedStyleId === item.id && <small className="rounded-full bg-secondary/25 px-2 py-0.5 text-[9px] uppercase tracking-[.08em] text-primary">AI pick</small>}</strong><small className="text-xs text-muted-foreground">{item.silhouette}</small></span>{styleId === item.id && <Check className="ml-auto text-primary" size={17} />}</button>)}</div>{generationError && <p className="mt-5 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm leading-6 text-destructive" role="alert">{generationError}</p>}<button onClick={generate} className="mt-8 flex w-full items-center justify-center gap-3 rounded-xl bg-primary px-6 py-4 text-sm font-bold text-primary-foreground transition hover:shadow-lg" data-testid="button-create-look">Create my real try-on <Sparkles size={16} /></button></div></div>}
       {step === 'generating' && <div className="mx-auto max-w-xl py-12 text-center"><div className="mx-auto h-64 w-64 animate-pulse overflow-hidden rounded-2xl bg-primary"><img src={chosenStyle.image} alt={`${chosenStyle.name} turban reference`} className="h-full w-full object-cover object-[center_20%]" /></div><h2 className="display-font mt-9 text-4xl">Preparing your preview</h2><p className="mt-4 text-muted-foreground">{status}</p><div className="mx-auto mt-8 h-1.5 max-w-xs overflow-hidden rounded-full bg-muted"><div className="h-full w-4/5 animate-pulse rounded-full bg-secondary" /></div></div>}
        {step === 'result' && <div className="grid gap-8 lg:grid-cols-[1.08fr_.92fr]"><div className="rounded-[1.5rem] bg-primary p-3 sm:p-5"><div className="relative overflow-hidden rounded-[1.1rem] bg-black">{generatedPhoto ? <img src={generatedPhoto} alt={`Photorealistic ${chosenStyle.name} turban try-on`} className="aspect-[4/5] w-full object-contain" /> : <div className="aspect-[4/5] bg-muted" />}<span className="absolute left-3 top-3 rounded-full bg-background/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[.1em] text-foreground">Generated try-on</span><span className="absolute bottom-3 left-3 rounded-full bg-primary/85 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[.1em] text-primary-foreground">AI image edit</span></div><div className="mt-4 flex items-center justify-between gap-4 text-primary-foreground"><div><p className="mono-font text-[10px] uppercase tracking-[.16em] text-secondary">Style direction</p><p className="mt-1 text-sm font-semibold">{chosenStyle.name} · {colourName}</p></div><div className="h-16 w-16 overflow-hidden rounded-xl bg-primary-foreground/10"><img src={chosenStyle.image} alt={`${chosenStyle.name} turban reference`} className="h-full w-full object-cover object-[center_20%]" /></div></div></div><div className="flex flex-col justify-center"><p className="mono-font text-[10px] uppercase tracking-[.16em] text-primary">Your personalized look</p><h2 className="display-font mt-4 text-5xl leading-none">{chosenStyle.name}</h2><p className="mt-5 text-base leading-7 text-muted-foreground">{chosenStyle.description} This {occasionLabel.toLowerCase()} direction gives your outfit a confident, considered finish.</p><div className="mt-6 rounded-2xl border border-secondary/40 bg-secondary/10 p-5"><p className="mono-font text-[10px] uppercase tracking-[.14em] text-primary">Your request</p><p className="mt-2 text-sm leading-6 text-foreground">{request || `A ${chosenStyle.silhouette.toLowerCase()} for ${occasionLabel.toLowerCase()}.`}</p></div>{storageNotice && <p className="mt-3 rounded-xl bg-muted px-4 py-3 text-xs leading-5 text-muted-foreground" role="status">{storageNotice}</p>}<ColourPicker value={colour} name={colourName} busy={isAdjusting} onChange={applyColourChange} resultMode /><AdjustmentPanel correction={correction} busy={isAdjusting} onCorrectionChange={setCorrection} onApply={applyCorrection} />{adjustmentStatus && <p className="mt-3 rounded-xl bg-secondary/10 px-4 py-3 text-xs leading-5 text-foreground" role="status">{adjustmentStatus}</p>}{adjustmentError && <p className="mt-3 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm leading-6 text-destructive" role="alert">{adjustmentError}</p>}<div className="mt-7 grid grid-cols-2 gap-3"><button onClick={tryAnother} disabled={isAdjusting} className="rounded-xl border border-border px-4 py-3 text-sm font-bold transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40">Try another</button>{isAdjusting ? <span className="rounded-xl bg-primary px-4 py-3 text-center text-sm font-bold text-primary-foreground/70" aria-disabled="true">Saving edit…</span> : <Link href="/looks" className="rounded-xl bg-primary px-4 py-3 text-center text-sm font-bold text-primary-foreground transition hover:shadow-lg" data-testid="link-view-my-looks">Save & view looks</Link>}</div><button onClick={() => { if (navigator.share) navigator.share({ title: 'My Turbans.ai look', text: `I’m exploring ${chosenStyle.name} in ${colourName} for ${occasionLabel.toLowerCase()}.` }); else window.alert('Your look is ready to share.'); }} className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-border px-4 py-3 text-sm font-semibold transition hover:bg-muted" data-testid="button-share-look">Share this look <UploadCloud size={15} /></button></div></div>}
       {step === 'upload' && <div className="mt-12 border-t border-border pt-6 text-xs leading-5 text-muted-foreground"><strong className="text-foreground">Privacy first.</strong> Photos are processed to create your requested result. You can delete your images and account at any time.</div>}
    </main>
  </div>;
}